import { Matrix3, Vector3 } from 'three';
import { pipe } from '@vaaas/fpts/function';
import * as v3 from './vector3';

/** create a new matrix */
export function of(
    a: number, b: number, c: number,
    d: number, e: number, f: number,
    g: number, h: number, i: number
): Matrix3 {
    return new Matrix3().set(a, b, c, d, e, f, g, h, i);
}

/** multiply two matrices, returning the result as a new matrix */
export function mult(a: Matrix3, b: Matrix3): Matrix3 {
    return new Matrix3().multiplyMatrices(a, b);
}

/** curried multiplication of two matrices */
export const cmult = (a: Matrix3) => (b: Matrix3) => mult(a, b);

/** Calculates the matrix that represents a rotation transformation around
 * vector v through origin with lattitude theta,longitude phi by an angle
 * a, where angles are measured according to Lentz's law (right
 * hand). Result is stored in "res" and returned.
 * IMPORTANT: The 3x3 matrix returned should be multiplied with a column vector,
 * not a row vector.
 */
export function rotationMatrix(theta: number, phi: number, a: number): Matrix3 {
    /* We will proceed as follows:
     * 1) Rotate around z axis until v lies on plane xz.
     * 2) Rotate around y axis until v lies on z axis.
     * 3) Rotate around z axis by a.
     * 4) Cancel out 2.
     * 5) Cancel out 1.
     */
    const costheta = Math.cos(theta);
    const sintheta = Math.sin(theta);
    const cosphi = Math.cos(phi);
    const sinphi = Math.sin(phi);
    const cosa = Math.cos(a);
    const sina = Math.sin(a);

    return pipe(
        // 1.Do a rotation around z by 2pi-phi
        of(
            cosphi, sinphi, 0.0,
            -sinphi, cosphi, 0.0,
            0.0, 0.0, 1.0,
        ),

        // 2.Do a rotation around y by 2pi-theta
        cmult(of(
            costheta, 0.0, -sintheta,
            0.0, 1.0, 0.0,
            sintheta, 0.0, costheta,
        )),

        // 3.Do a rotation around z by a
        cmult(of(
            cosa, -sina, 0.0,
            sina, cosa, 0.0,
            0.0, 0.0, 1.0,
        )),

        // 4.Do a rotation around y by theta
        cmult(of(
            costheta, 0.0, sintheta,
            0.0, 1.0, 0.0,
            -sintheta, 0.0, costheta
        )),

        // 5.Do a rotation around z by phi
        cmult(of(
            cosphi, -sinphi, 0.0,
            sinphi, cosphi, 0.0,
            0.0, 0.0, 1.0,
        )),
    );
}

/** return a copy of a matrix */
export function copy(x: Matrix3): Matrix3 {
    return new Matrix3().copy(x);
}

/** return a new, transposed matrix */
export function transpose(x: Matrix3): Matrix3 {
    return copy(x).transpose();
}

/** return a rotation matrix around a vector, by counter-clockwise angle
 *
 * - `d` — the vector
 * - `angle` — the angle
 */
export function vectorRotation(d: Vector3, angle: number): Matrix3 {
    const psi = v3.angle(d, v3.Axes.Z);
    if (psi > Math.PI / 12 && psi < (11 * Math.PI) / 12) {
        return rotationMatrix(...v3.toAngles(d), angle);
    } else {
        const Rx = rotationMatrix(Math.PI / 2.0, 0, Math.PI / 2.0);
        const Rtmp = rotationMatrix(
            ...v3.toAngles(v3.multMatrix(d, Rx)),
            angle
        );
        return mult(
            transpose(Rx),
            mult(Rtmp, Rx)
        );
    }
}

/** create a matrix from 3 vectors */
export function fromVectors(a: Vector3, b: Vector3, c: Vector3): Matrix3 {
    return new Matrix3().set(
        a.x, b.x, c.x,
        a.y, b.y, c.y,
        a.z, b.z, c.z,
    );
}

/** given two vector triplets, return a rotation matrix that maps the first triplet onto the second triplet
 *
 * i.e.: `R va = vd, R vb = ve, R vc = vf`
 *
 * the vectors *must* be unit and pairwise orthogonal:
 * - `va` perpendicular `vb`
 * - `va` perpendicular `vc`
 * - `vb` perpendicular `vc`
 * - `vd` perpendicular `ve`
 * - `vd` perpendicular `vf`
 * - `ve` perpendicular `vf`
 */
export function rotationMatrixFromVecTriplets(va: Vector3, vb: Vector3, vc: Vector3, vd: Vector3, ve: Vector3, vf: Vector3) {
    return pipe(
        fromVectors(va, vb, vc),
        transpose,
        cmult(fromVectors(vd, ve, vf)),
    );
}


/** calculates a rotation matrix that translates `a` into `b`
 *
 * the vectors should be unit length direction vectors
 */
export function rotationMatrixBetweenVectors(d1: Vector3, d2: Vector3): Matrix3 {
    const local_epsilon = 1e-13;
    const a = v3.cross(d1, d2);
    if (a.length() > local_epsilon)
        a.normalize();
    else
        a.crossVectors(d1, v3.random().normalize()).normalize();
    const c1 = v3.cross(a, d1);
    const c2 = v3.cross(a, d2);
    return rotationMatrixFromVecTriplets(a, d1, c1, a, d2, c2);
}
